import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Info, Lock, Phone, Video } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toApiError } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  conversationQuery,
  fetchOlderMessages,
  latestMessagesQuery,
  markRead,
  useDeleteMessage,
} from '../api'
import { useCalls } from '../callContext'
import { dayLabel, isSameDay, roleLabel } from '../format'
import type { ChatMessage, ConversationDetail } from '../types'
import { Composer } from './Composer'
import { ConversationInfo } from './ConversationInfo'
import { ConversationAvatar } from './ConversationList'
import { MessageItem } from './MessageItem'

/** One conversation: header (calls, details), messages polled every few seconds, composer. */
export function Thread({ id, backHref }: { id: number; backHref: string }) {
  const { t } = useTranslation('messaging')
  const detail = useQuery(conversationQuery(id))
  const latest = useQuery(latestMessagesQuery(id))
  const [older, setOlder] = useState<ChatMessage[]>([])
  const [olderDone, setOlderDone] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [editing, setEditing] = useState<ChatMessage | null>(null)
  const [deleting, setDeleting] = useState<ChatMessage | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const remove = useDeleteMessage()
  const calls = useCalls()
  const scroller = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)
  const lastRead = useRef(0)

  const messages = useMemo(() => {
    const byId = new Map<number, ChatMessage>()
    for (const m of [...older, ...(latest.data ?? [])]) byId.set(m.id, m)
    return [...byId.values()].sort((a, b) => a.id - b.id)
  }, [older, latest.data])
  const lastId = messages.at(-1)?.id ?? 0

  // Follow new messages while the reader is at the bottom.
  useLayoutEffect(() => {
    const el = scroller.current
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight
  }, [lastId, detail.data?.id])

  // Mark read while the conversation is on screen.
  useEffect(() => {
    if (lastId === 0 || lastId <= lastRead.current || document.visibilityState !== 'visible') return
    lastRead.current = lastId
    void markRead(id, lastId).catch(() => undefined)
  }, [id, lastId])

  const loadOlder = useCallback(async () => {
    const first = messages[0]
    if (!first || loadingOlder) return
    setLoadingOlder(true)
    const el = scroller.current
    const before = el ? el.scrollHeight - el.scrollTop : 0
    try {
      const page = await fetchOlderMessages(id, first.id)
      if (page.length < 50) setOlderDone(true)
      setOlder((o) => [...page, ...o])
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - before
      })
    } catch (e) {
      toast.error(t('Could not load earlier messages'), toApiError(e).message)
    } finally {
      setLoadingOlder(false)
    }
  }, [id, messages, loadingOlder, t])

  if (detail.isPending) return <LoadingState className="h-full" />
  if (detail.error) return <ErrorState onRetry={() => void detail.refetch()} />
  const c = detail.data
  const hasMore = !olderDone && (latest.data?.length ?? 0) >= 50

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ThreadHeader conversation={c} backHref={backHref} onInfo={() => setInfoOpen(true)} />
      <div
        ref={scroller}
        className="min-h-0 flex-1 overflow-y-auto bg-canvas px-3 py-4 sm:px-5"
        onScroll={(e) => {
          const el = e.currentTarget
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
        }}
        aria-live="polite"
        aria-relevant="additions"
      >
        {hasMore ? (
          <div className="mb-3 flex justify-center">
            <Button size="sm" variant="secondary" onClick={() => void loadOlder()} loading={loadingOlder}>
              {t('Load earlier messages')}
            </Button>
          </div>
        ) : null}
        {latest.isPending ? (
          <LoadingState />
        ) : messages.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted">
            {c.kind === 'class'
              ? t('Nothing posted in this class room yet.')
              : t('No messages yet. Say hello!')}
          </p>
        ) : (
          messages.map((m, i) => {
            const prev = messages[i - 1]
            const newDay = !prev || !isSameDay(prev.createdAt, m.createdAt)
            const continued =
              !newDay &&
              prev.kind === 'text' &&
              m.kind === 'text' &&
              prev.sender?.id === m.sender?.id &&
              new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000
            return (
              <div key={m.id}>
                {newDay ? (
                  <div className="my-4 flex items-center gap-3 text-2xs font-medium text-subtle uppercase">
                    <span className="h-px flex-1 bg-line" />
                    {dayLabel(m.createdAt)}
                    <span className="h-px flex-1 bg-line" />
                  </div>
                ) : null}
                <MessageItem
                  message={m}
                  showSender={c.kind !== 'direct'}
                  continued={continued}
                  onEdit={setEditing}
                  onDelete={setDeleting}
                  onJoinCall={(msg) =>
                    msg.call
                      ? void calls.join(msg.call.id, c.title, msg.call.video, c.kind === 'direct')
                      : undefined
                  }
                />
              </div>
            )
          })
        )}
      </div>
      {c.canPost ? (
        <Composer
          conversationId={id}
          editing={editing}
          onCancelEdit={() => setEditing(null)}
          onSent={() => {
            stickToBottom.current = true
          }}
        />
      ) : (
        <p className="flex items-center justify-center gap-2 border-t border-line bg-surface px-4 py-4 text-sm text-muted">
          <Lock className="size-4" aria-hidden="true" />
          {c.kind === 'class'
            ? t('Only teachers can post in this class room right now.')
            : t('You can no longer reply in this conversation.')}
        </p>
      )}
      <ConversationInfo conversation={c} open={infoOpen} onOpenChange={setInfoOpen} backHref={backHref} />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={t('Delete this message?')}
        description={t('Everyone in the conversation will see “Message deleted” instead.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!deleting) return
          remove.mutate(deleting.id, {
            onSuccess: () => setDeleting(null),
            onError: (e) => toast.error(t('Could not delete the message'), toApiError(e).message),
          })
        }}
      />
    </div>
  )
}

function ThreadHeader({
  conversation: c,
  backHref,
  onInfo,
}: {
  conversation: ConversationDetail
  backHref: string
  onInfo: () => void
}) {
  const { t } = useTranslation('messaging')
  const calls = useCalls()
  const direct = c.kind === 'direct'
  const subtitle = direct
    ? c.other
      ? roleLabel(c.other.role)
      : ''
    : c.kind === 'class'
      ? t('Class room · {{count}} members', { count: c.memberCount })
      : t('{{count}} members', { count: c.memberCount })
  const ongoing = c.ongoingCall
  const inIt = ongoing !== null && calls.activeCallId === ongoing.id

  return (
    <>
      <header className="flex items-center gap-3 border-b border-line bg-surface px-3 py-2.5 sm:px-4">
        <Link
          to={backHref}
          className="grid size-9 place-items-center rounded-full text-muted hover:bg-surface-muted lg:hidden"
          aria-label={t('Back to conversations')}
        >
          <ArrowLeft className="size-5" />
        </Link>
        <button type="button" onClick={onInfo} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <ConversationAvatar kind={c.kind} title={c.title} src={c.other?.avatarUrl} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink">{c.title}</span>
            <span className="block truncate text-xs text-muted">{subtitle}</span>
          </span>
        </button>
        {c.canStartCall && calls.available && !ongoing ? (
          <>
            <IconButton
              label={c.kind === 'class' ? t('Start a live class (audio)') : t('Audio call')}
              icon={<Phone />}
              variant="ghost"
              onClick={() => void calls.start(c.id, false, c.title, direct)}
            />
            <IconButton
              label={c.kind === 'class' ? t('Start a live class (video)') : t('Video call')}
              icon={<Video />}
              variant="ghost"
              onClick={() => void calls.start(c.id, true, c.title, direct)}
            />
          </>
        ) : null}
        <IconButton label={t('Details')} icon={<Info />} variant="ghost" onClick={onInfo} />
      </header>
      {ongoing && !inIt ? (
        <div className="border-success-200 text-success-800 flex items-center gap-3 border-b bg-success-50 px-4 py-2 text-sm">
          <span className="relative flex size-2.5">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-500 opacity-60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-success-600" />
          </span>
          <span className="flex-1">
            {c.kind === 'class'
              ? t('A live class is in progress.')
              : ongoing.video
                ? t('A video call is in progress.')
                : t('An audio call is in progress.')}
          </span>
          {calls.available ? (
            <Button
              size="sm"
              className="bg-success-600 hover:bg-success-700"
              leftIcon={ongoing.video ? <Video /> : <Phone />}
              onClick={() => void calls.join(ongoing.id, c.title, ongoing.video, direct)}
            >
              {t('Join')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
